/**
 * CustomSelect — Professional, enterprise-grade dropdown select.
 *
 * Design:
 *   - White background, border-slate-200, soft shadow
 *   - Portal-based floating menu (never clipped)
 *   - Teal/Indigo hover highlights
 *   - Search filter when > 5 items
 *   - Keyboard navigation (arrows, Enter, Escape)
 *   - Focus ring (ring-2 ring-indigo-500/20)
 *   - Compact text-sm / tight padding ("Enterprise Dense" theme)
 */

import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search } from 'lucide-react';
import { cn } from '../../ui/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

export interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Show search when options exceed this (default 5) */
  searchThreshold?: number;
  /** Leading icon */
  icon?: React.ReactNode;
  /** Label shown above the trigger */
  label?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CustomSelect({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabled,
  className,
  searchThreshold = 5,
  icon,
  label,
}: CustomSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [hlIdx, setHlIdx] = useState(-1);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selected = options.find(o => o.value === value);
  const showSearch = options.length > searchThreshold;

  const filtered = search
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  // ── Position ──────────────────────────────────────────────────────────────

  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const [positioned, setPositioned] = useState(false);

  const recalcPos = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const panelH = Math.min(filtered.length * 34 + (showSearch ? 44 : 0) + 12, 300);
    const below = window.innerHeight - r.bottom - 8;
    const above = below < panelH && r.top > panelH;
    setPos({
      top: above ? r.top - panelH - 4 : r.bottom + 4,
      left: r.left,
      width: Math.max(r.width, 200),
    });
  }, [filtered.length, showSearch]);

  // Calculate position synchronously before paint to prevent flicker
  useLayoutEffect(() => {
    if (open) { recalcPos(); setPositioned(true); }
    else setPositioned(false);
  }, [open, recalcPos]);

  // Recalc on scroll / resize while open
  useEffect(() => {
    if (!open) return;
    const handler = () => recalcPos();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => { window.removeEventListener('scroll', handler, true); window.removeEventListener('resize', handler); };
  }, [open, recalcPos]);

  // ── Open / Close ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (open) { setSearch(''); setHlIdx(-1); }
    if (open && showSearch) setTimeout(() => searchRef.current?.focus(), 0);
  }, [open, showSearch]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // ── Keyboard ──────────────────────────────────────────────────────────────

  const onKey = useCallback((e: React.KeyboardEvent) => {
    if (!open) {
      if (['Enter', ' ', 'ArrowDown'].includes(e.key)) { e.preventDefault(); setOpen(true); }
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHlIdx(i => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHlIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && hlIdx >= 0 && hlIdx < filtered.length) {
      e.preventDefault();
      const opt = filtered[hlIdx];
      if (!opt.disabled) { onChange(opt.value); setOpen(false); }
    }
  }, [open, hlIdx, filtered, onChange]);

  useEffect(() => {
    if (hlIdx < 0 || !listRef.current) return;
    const el = listRef.current.children[hlIdx] as HTMLElement;
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [hlIdx]);

  // ── Pick ──────────────────────────────────────────────────────────────────

  const pick = (val: string) => { onChange(val); setOpen(false); };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className={label ? 'space-y-1' : undefined}>
      {label && <label className="block text-[10px] font-bold text-zinc-400 uppercase tracking-widest">{label}</label>}

      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        onKeyDown={onKey}
        className={cn(
          'flex items-center gap-2 w-full px-3 py-[7px] bg-white border border-slate-200 rounded-lg text-sm text-left transition-all',
          'shadow-[0_1px_2px_rgba(0,0,0,0.04)]',
          'focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400',
          'hover:border-slate-300',
          disabled && 'opacity-50 cursor-not-allowed bg-slate-50',
          open && 'ring-2 ring-indigo-500/20 border-indigo-400',
          className,
        )}
      >
        {icon && <span className="shrink-0 text-slate-400 [&>svg]:w-3.5 [&>svg]:h-3.5">{icon}</span>}
        <span className={cn('flex-1 truncate', !selected ? 'text-slate-400' : 'text-slate-800')}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          size={14}
          className={cn('shrink-0 text-slate-400 transition-transform duration-200', open && 'rotate-180')}
        />
      </button>

      {/* Floating panel via portal */}
      {open && createPortal(
        <div
          ref={panelRef}
          onKeyDown={onKey}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999 }}
          className={cn(
            'bg-white border border-slate-200 rounded-lg shadow-lg shadow-slate-200/60 overflow-hidden',
            positioned ? 'opacity-100' : 'opacity-0 pointer-events-none',
          )}
        >
          {/* Search */}
          {showSearch && (
            <div className="p-1.5 border-b border-slate-100">
              <div className="relative">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={e => { setSearch(e.target.value); setHlIdx(0); }}
                  placeholder="Search…"
                  className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 transition-all"
                />
              </div>
            </div>
          )}

          {/* Option list */}
          <div ref={listRef} className="max-h-[240px] overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-5 text-center text-xs text-slate-400">No results found</div>
            ) : (
              filtered.map((opt, idx) => {
                const isSel = opt.value === value;
                const isHl = idx === hlIdx;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={opt.disabled}
                    onClick={() => !opt.disabled && pick(opt.value)}
                    onMouseEnter={() => setHlIdx(idx)}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-[6px] text-sm text-left transition-colors',
                      isHl && !opt.disabled && 'bg-indigo-50 text-indigo-700',
                      isSel && !isHl && 'bg-slate-50 font-medium',
                      !isSel && !isHl && 'text-slate-700',
                      opt.disabled && 'opacity-40 cursor-not-allowed',
                    )}
                  >
                    {opt.icon && <span className="shrink-0 text-slate-400 [&>svg]:w-3.5 [&>svg]:h-3.5">{opt.icon}</span>}
                    <span className="flex-1 truncate">{opt.label}</span>
                    {isSel && <Check size={14} className="shrink-0 text-indigo-600" />}
                  </button>
                );
              })
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
